// React
import { useState, useEffect, memo } from 'react';

// Store 
import useCoachStore from '../../store/coachStore.js';
import useSiteStore from '../../store/siteStore.js';

// Hooks
import { useAppTheme } from '../../hooks/useAppTheme.js';

// Components
import { EditOutlined, RadioButtonCheckedOutlined, PersonOutline, CakeOutlined, InfoOutlined, DeleteOutline, ExpandMoreOutlined, EmailOutlined, PhoneOutlined } from '@mui/icons-material';
import { Accordion, AccordionSummary, AccordionDetails, Avatar, Box, IconButton, List, Typography, Card, CardContent, Chip, Tooltip } from '@mui/material';

// Style
import { blue, green, grey, orange, red, yellow }
  from '@mui/material/colors/index.js';
import { darkTheme } from '../../styles/colorSchemes/index.js';

// Page
import EditUser from '../dialog/EditUser.js';

// Other 
import { getAthletesOfNationalCoach } from '../../service/httpService/athlete/index.js';
import { deleteProfile } from '../../service/httpService/profile/index.js';
import { getConsistentAvatarColor } from '../../utils/index.js';

const AthleteList = (props) => {
  const { athletes, handleSelectAthlete, isEditable, isMobile, isNationalCoach, search, onRefreshUserLists } = props;

  const { squads, openEditUser, setOpenEditUser, expandedAccordions, toggleAccordion } = useCoachStore();

  // Optimized store access
  const selectedSortingCriteria = useSiteStore(state => state.selectedSortingCriteria);
  const selectedAthlete = useSiteStore(state => state.selectedAthlete);

  // Use centralized theme
  const { isDarkMode: athleteListDarkMode } = useAppTheme();

  const [nationalAthletes, setNationalAthletes] = useState([]);
  const [selectedColor, setSelectedColor] = useState();
  const [athleteToEdit, setAthleteToEdit] = useState(null);

  const renderStatus = (status) => {
    const colorMap = {
      1: red[500],
      2: yellow[500],
      3: green[500],
    };

    return (
      <RadioButtonCheckedOutlined sx={{ color: colorMap[status] || grey[500] }} />
    );
  };

  useEffect(() => {
    if (isNationalCoach === true) {
      getAthletesOfNationalCoach()
        .then(response => {
          let national = response.data;
          setNationalAthletes(national);
        }).catch(() => { setNationalAthletes([]); });
    }
  }, [isNationalCoach]);


  const convertBufferToBase64String = (athlete) => {
    return btoa(String.fromCharCode(...new Uint8Array(athlete.profile.picture.data)));
  }

  function getComparisonFunction(sortCriteria) {
    switch (sortCriteria) {
      case 'NachnameAuf':
        return (a, b) => {
          // Incomplete athletes go to the end
          if (!a.profile.lastName && !b.profile.lastName) return 0;
          if (!a.profile.lastName) return 1;
          if (!b.profile.lastName) return -1;
          return String(a.profile.lastName).localeCompare(String(b.profile.lastName));
        };
      case 'NachnameAb':
        return (a, b) => {
          // Incomplete athletes go to the end
          if (!a.profile.lastName && !b.profile.lastName) return 0;
          if (!a.profile.lastName) return 1;
          if (!b.profile.lastName) return -1;
          return String(b.profile.lastName).localeCompare(String(a.profile.lastName));
        };
      case 'GeburtsdatumAb':
        return function (a, b) {
          const dateA = new Date(a.profile.dateOfBirth);
          const dateB = new Date(b.profile.dateOfBirth);
          return dateA - dateB;
        };
      case 'GeburtsdatumAuf':
        return function (a, b) {
          const dateA = new Date(a.profile.dateOfBirth);
          const dateB = new Date(b.profile.dateOfBirth);
          return dateB - dateA;
        };
      case 'AmpelAuf':
        return (a, b) => a.status - b.status;
      case 'AmpelAb':
        return (a, b) => b.status - a.status;

      case 'RueckmeldedatumAuf':
        return function (a, b) {
          const dateA = new Date(a.dateOfLastFeedback);
          const dateB = new Date(b.dateOfLastFeedback);
          return dateB - dateA;
        };
      case 'RueckmeldedatumAb':
        return function (a, b) {
          const dateA = new Date(a.dateOfLastFeedback);
          const dateB = new Date(b.dateOfLastFeedback);
          return dateA - dateB;
        };

      default:
        return (a, b) => 0;
    }
  }
  function convertDateToRelativeString(dateInput) {
    // Convert the input to a Date object if it's not already
    const inputDate = new Date(dateInput);
    if (isNaN(inputDate)) return "Invalid date";

    let neverDate = new Date('1900-01-01');

    if (inputDate.getTime() === neverDate.getTime())
      return "Noch keine\nRückmeldung\nvorhanden"

    // Get the current date
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0); // Reset time to midnight
    inputDate.setHours(0, 0, 0, 0);   // Reset time to midnight

    // Calculate the difference in milliseconds
    const differenceInMilliseconds = currentDate - inputDate;

    // Convert the difference to days
    const differenceInDays = Math.round(differenceInMilliseconds / (1000 * 60 * 60 * 24));

    // Determine the output based on the difference in days
    if (differenceInDays === 0) {
      return `Letzte\nRückmeldung:\nheute`;
    } else if (differenceInDays === 1) {
      return `Letzte\nRückmeldung:\ngestern`;
    } else if (differenceInDays === 2) {
      return `Letzte\nRückmeldung:\nvorgestern`;
    } else if (differenceInDays > 2) {
      return `Letzte\nRückmeldung:\nvor ${differenceInDays} Tagen`;
    } else {
      return `Letzte\nRückmeldung:\nin der Zukunft`; // Handle future dates if needed
    }
  }

  function getAge(dateOfBirth) {
    // Parse the dateOfBirth into a Date object
    const birthDate = new Date(dateOfBirth);
    if (isNaN(birthDate)) {
      throw new Error("Invalid date format");
    }
    // Get today's date
    const today = new Date();

    // Calculate the age
    let age = today.getFullYear() - birthDate.getFullYear();

    // Adjust if the birthday hasn't occurred yet this year
    const monthDifference = today.getMonth() - birthDate.getMonth();
    const dayDifference = today.getDate() - birthDate.getDate();
    if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
      age--;
    }

    return age;
  }

  return (
    <List
      dense
      sx={{
        '& ul': { padding: 0 },
        '&.MuiList-root': { paddingTop: 0 }
      }}
    >
      {athleteToEdit !== null && selectedColor !== undefined && (
        <EditUser
          color={selectedColor}
          open={openEditUser}
          onClose={() => setOpenEditUser(false)}
          profileType={'athlete'}
          person={athleteToEdit}
          athletes={athletes}
          onSuccess={onRefreshUserLists}
        />
      )}
      {squads.map((squad, indexSquads) => (
        <Accordion
          key={indexSquads}
          expanded={expandedAccordions[squad] || false}
          onChange={() => toggleAccordion(squad)}
          sx={{
            borderRadius: 2,
            mb: 2,
            overflow: 'hidden',
            boxShadow: athleteListDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
            backgroundColor: athleteListDarkMode ? darkTheme.cardBg : undefined,
            '&:before': {
              display: 'none',
            },
            '&.Mui-expanded': {
              margin: '0 0 16px 0',
            }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreOutlined />}
            aria-controls={`panel${indexSquads}-content`}
            id={`panel${indexSquads}-header`}
            sx={{
              bgcolor: athleteListDarkMode ? darkTheme.accordionBg : 'rgba(0, 0, 0, 0.02)',
              borderBottom: athleteListDarkMode ? `1px solid ${darkTheme.accordionBorder}` : '1px solid rgba(0, 0, 0, 0.08)',
              '&:hover': {
                bgcolor: athleteListDarkMode ? darkTheme.cardHover : 'rgba(0, 0, 0, 0.04)',
              },
              '& .MuiAccordionSummary-content': {
                my: 1.5,
              },
              '& .MuiAccordionSummary-expandIconWrapper': {
                color: athleteListDarkMode ? darkTheme.textSecondary : undefined,
              }
            }}
          >
            <Typography fontWeight={600} color={athleteListDarkMode ? darkTheme.textPrimary : "text.primary"}>
              {`${squad} (${athletes
                .filter((athlete) => athlete.squad === squad)
                .filter(
                  (athlete) =>
                    (athlete.profile.lastName?.toLowerCase() || '').includes(String(search).toLowerCase()) ||
                    (athlete.profile.firstName?.toLowerCase() || '').includes(String(search).toLowerCase()) ||
                    (athlete.profile.email?.toLowerCase() || '').includes(String(search).toLowerCase())
                ).length})`}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0, bgcolor: athleteListDarkMode ? darkTheme.bg : undefined }}>
            <Box>
              {athletes
                .filter((athlete) => athlete.squad === squad)
                .filter(
                  (athlete) =>
                    (athlete.profile.lastName?.toLowerCase() || '').includes(String(search).toLowerCase()) ||
                    (athlete.profile.firstName?.toLowerCase() || '').includes(String(search).toLowerCase()) ||
                    (athlete.profile.email?.toLowerCase() || '').includes(String(search).toLowerCase())
                )
                .sort(getComparisonFunction(selectedSortingCriteria))
                .map((athlete, index) => (
                  <Box key={index}>
                    <Card
                      elevation={0}
                      sx={{
                        background: athleteListDarkMode
                          ? athlete?.profile.email === selectedAthlete?.profile.email
                            ? `linear-gradient(90deg, ${darkTheme.accent}22 0%, ${darkTheme.cardBg} 100%)`
                            : (!athlete.profile.lastName || !athlete.profile.firstName)
                              ? `linear-gradient(90deg, ${orange[900]}44 0%, ${darkTheme.cardBg} 100%)`
                              : darkTheme.cardBg
                          : athlete?.profile.email === selectedAthlete?.profile.email
                            ? `linear-gradient(90deg, ${blue[50]} 0%, rgba(255, 255, 255, 0) 100%)`
                            : (!athlete.profile.lastName || !athlete.profile.firstName)
                              ? `linear-gradient(90deg, ${orange[50]} 0%, rgba(255, 255, 255, 0) 100%)`
                              : nationalAthletes.findIndex(a => a.profile.email === athlete.profile.email) === -1
                                ? 'white'
                                : isNationalCoach
                                  ? 'white'
                                  : 'white',
                        border: athleteListDarkMode ? `1px solid ${darkTheme.cardBorder}` : '1px solid rgba(0, 0, 0, 0.08)',
                        borderLeft: athleteListDarkMode
                          ? athlete?.profile.email === selectedAthlete?.profile.email
                            ? `4px solid ${darkTheme.accent}`
                            : (!athlete.profile.lastName || !athlete.profile.firstName)
                              ? `4px solid ${orange[700]}`
                              : `1px solid ${darkTheme.cardBorder}`
                          : athlete?.profile.email === selectedAthlete?.profile.email
                            ? `4px solid ${blue[500]}`
                            : (!athlete.profile.lastName || !athlete.profile.firstName)
                              ? `4px solid ${orange[500]}`
                              : '1px solid rgba(0, 0, 0, 0.08)',
                        borderRadius: 2,
                        transition: 'all 0.2s ease',
                        boxShadow: athleteListDarkMode ? '0 1px 3px rgba(0, 0, 0, 0.2)' : '0 1px 3px rgba(0, 0, 0, 0.05)',
                        '&:hover': {
                          bgcolor: athleteListDarkMode ? darkTheme.cardHover : 'rgba(0, 0, 0, 0.02)',
                          boxShadow: athleteListDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            cursor: (!athlete.profile.lastName || !athlete.profile.firstName) ? 'default' : 'pointer'
                          }}
                          onClick={(!athlete.profile.lastName || !athlete.profile.firstName) ? undefined : () => {
                            handleSelectAthlete(athlete, isMobile);
                          }}
                        >
                          {/* Avatar Section */}
                          <Box sx={{ position: 'relative' }}>
                            <Tooltip
                              placement="right"
                              slotProps={{
                                tooltip: {
                                  sx: {
                                    bgcolor: athleteListDarkMode ? darkTheme.cardBg : 'common.white',
                                    color: athleteListDarkMode ? darkTheme.textPrimary : 'rgba(0, 0, 0, 0.87)',
                                    boxShadow: athleteListDarkMode ? '0 4px 20px rgba(0, 0, 0, 0.35)' : '0 4px 20px rgba(0, 0, 0, 0.15)',
                                    border: `1px solid ${athleteListDarkMode ? darkTheme.cardBorder : '#e0e0e0'}`,
                                    borderRadius: '10px',
                                    p: 0,
                                    overflow: 'hidden',
                                    maxWidth: 'none',
                                    width: 'fit-content',
                                  }
                                }
                              }}
                              title={
                                <Box sx={{ overflow: 'hidden' }}>
                                  <Box
                                    sx={{
                                      bgcolor: getConsistentAvatarColor(athlete),
                                      px: 1.5,
                                      py: 0.75,
                                      textAlign: 'center',
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      fontWeight={700}
                                      sx={{
                                        color: athleteListDarkMode ? 'rgba(0, 0, 0, 0.87)' : 'white',
                                        fontSize: '0.82rem',
                                      }}
                                    >
                                      Kontakt
                                    </Typography>
                                  </Box>

                                  <Box sx={{ px: 1.5, py: 1, minWidth: '190px', width: 'max-content' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: '78px' }}>
                                        <EmailOutlined sx={{ fontSize: 15, color: athleteListDarkMode ? darkTheme.textSecondary : '#666' }} />
                                        <Typography variant="caption" sx={{ color: athleteListDarkMode ? darkTheme.textSecondary : '#666', fontSize: '0.75rem' }}>
                                          E-Mail
                                        </Typography>
                                      </Box>
                                      <Typography variant="caption" fontWeight={600} sx={{ color: athleteListDarkMode ? darkTheme.textPrimary : 'rgba(0, 0, 0, 0.87)', fontSize: '0.75rem', textAlign: 'right', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                                        {athlete.profile.email || '-'}
                                      </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: '78px' }}>
                                        <PhoneOutlined sx={{ fontSize: 15, color: athleteListDarkMode ? darkTheme.textSecondary : '#666' }} />
                                        <Typography variant="caption" sx={{ color: athleteListDarkMode ? darkTheme.textSecondary : '#666', fontSize: '0.75rem' }}>
                                          Telefon
                                        </Typography>
                                      </Box>
                                      <Typography variant="caption" fontWeight={600} sx={{ color: athleteListDarkMode ? darkTheme.textPrimary : 'rgba(0, 0, 0, 0.87)', fontSize: '0.75rem', textAlign: 'right', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                                        {athlete.profile.telephone || '-'}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              }
                            >
                              <Box sx={{ display: 'inline-flex' }}>
                                <Avatar
                                  src={`data:image/png;base64,${convertBufferToBase64String(athlete)}`}
                                  sx={{
                                    width: 56,
                                    height: 56,
                                    bgcolor: getConsistentAvatarColor(athlete),
                                    color: athleteListDarkMode ? darkTheme.accordionBg : 'white',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                    cursor: (isEditable && athlete.profile.lastName && athlete.profile.firstName)
                                      ? 'pointer'
                                      : 'default',
                                    transition: 'transform 0.2s ease',
                                    '&:hover': { transform: 'scale(1.05)' },
                                  }}
                                  onClick={
                                    (isEditable && athlete.profile.lastName && athlete.profile.firstName)
                                      ? (e) => {
                                        e.stopPropagation();
                                        setOpenEditUser(true);
                                        setAthleteToEdit(athlete);
                                        setSelectedColor(getConsistentAvatarColor(athlete));
                                      }
                                      : undefined
                                  }
                                />
                              </Box>
                            </Tooltip>

                            {/*Edit-Icon*/}
                            {(isEditable && athlete.profile.lastName && athlete.profile.firstName) && (
                              <IconButton
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  bottom: -4,
                                  right: -4,
                                  bgcolor: athleteListDarkMode ? darkTheme.cardBg : 'white',
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                                  width: 24,
                                  height: 24,
                                  '&:hover': {
                                    bgcolor: athleteListDarkMode ? darkTheme.accent : blue[50],
                                    color: athleteListDarkMode ? '#1a1a1a' : blue[700],
                                  }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenEditUser(true);
                                  setAthleteToEdit(athlete);
                                  setSelectedColor(getConsistentAvatarColor(athlete));
                                }}
                              >
                                <EditOutlined sx={{ fontSize: 14, color: athleteListDarkMode ? darkTheme.textPrimary : undefined }} />
                              </IconButton>
                            )}
                          </Box>


                          {/* Info Section */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25, ml: -0.25, flexWrap: 'wrap' }}>
                              <PersonOutline sx={{ fontSize: 16, color: athleteListDarkMode ? darkTheme.textSecondary : 'action.active' }} />
                              <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '0.95rem', color: athleteListDarkMode ? darkTheme.textPrimary : undefined }}>
                                {athlete.profile.lastName && athlete.profile.firstName
                                  ? `${athlete.profile.lastName}, ${athlete.profile.firstName}`
                                  : 'Noch nicht registriert'}
                              </Typography>
                              {(!athlete.profile.lastName || !athlete.profile.firstName) && (
                                <Chip
                                  label="Ausstehend"
                                  size="small"
                                  sx={{
                                    bgcolor: athleteListDarkMode ? orange[900] : orange[100],
                                    color: athleteListDarkMode ? orange[200] : orange[800],
                                    fontWeight: 600,
                                    fontSize: '0.65rem',
                                    height: '18px'
                                  }}
                                />
                              )}
                            </Box>

                            {(!athlete.profile.lastName || !athlete.profile.firstName) ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <CakeOutlined sx={{ fontSize: 14, color: athleteListDarkMode ? darkTheme.textSecondary : 'action.active' }} />
                                  <Typography variant="caption" sx={{ fontSize: '0.8rem', color: athleteListDarkMode ? darkTheme.textSecondary : 'text.secondary' }}>
                                    {athlete.profile.dateOfBirth
                                      ? `${getAge(athlete.profile.dateOfBirth)} Jahre`
                                      : 'Alter ausstehend'}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <InfoOutlined sx={{ fontSize: 14, color: athleteListDarkMode ? darkTheme.textSecondary : 'action.active' }} />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: '0.75rem',
                                      lineHeight: 1.2,
                                      color: athleteListDarkMode ? darkTheme.textSecondary : 'text.secondary'
                                    }}
                                  >
                                    {athlete.profile.email}
                                  </Typography>
                                </Box>
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <CakeOutlined sx={{ fontSize: 14, color: athleteListDarkMode ? darkTheme.textSecondary : 'action.active' }} />
                                  <Typography variant="caption" sx={{ fontSize: '0.8rem', color: athleteListDarkMode ? darkTheme.textSecondary : 'text.secondary' }}>
                                    {athlete.profile.dateOfBirth
                                      ? `${getAge(athlete.profile.dateOfBirth)} Jahre`
                                      : 'Alter ausstehend'}
                                  </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <InfoOutlined sx={{ fontSize: 14, color: athleteListDarkMode ? darkTheme.textSecondary : 'action.active', mt: '-5px' }} />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      mt: '-5px',
                                      fontSize: '0.75rem',
                                      lineHeight: 1.2,
                                      color: athleteListDarkMode ? darkTheme.textSecondary : 'text.secondary'
                                    }}
                                  >
                                    {convertDateToRelativeString(athlete.dateOfLastFeedback).replace(/\n/g, ' ')}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </Box>

                          {/* Status Section or Delete Button */}
                          {(!athlete.profile.lastName || !athlete.profile.firstName) ? (
                            <IconButton
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await deleteProfile(athlete.profile.email);
                                  if (onRefreshUserLists) {
                                    onRefreshUserLists();
                                  }
                                } catch (error) {
                                  console.error('Failed to delete athlete:', error);
                                }
                              }}
                              sx={{
                                color: red[600],

                                width: 40,
                                height: 40,
                                '&:hover': {
                                  bgcolor: red[50],
                                  color: red[600],
                                }
                              }}
                            >
                              <DeleteOutline sx={{ fontSize: 24 }} />
                            </IconButton>
                          ) : (
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                              px: 1.5,
                              py: 0.75,
                              borderRadius: 1.5,
                              bgcolor: athleteListDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                              border: athleteListDarkMode ? `1px solid ${darkTheme.cardBorder}` : '1px solid rgba(0, 0, 0, 0.08)',
                            }}>
                              {renderStatus(athlete.status)}
                              <Typography variant="caption" fontWeight={500} sx={{ fontSize: '0.75rem', color: athleteListDarkMode ? darkTheme.textSecondary : 'text.secondary' }}>
                                Status
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </List>
  );
}

export default memo(AthleteList);
